import { isPlatformBrowser } from '@angular/common';
import type { ElementRef, OnDestroy, OnInit, QueryList } from '@angular/core';
import { ChangeDetectorRef, Component, PLATFORM_ID, ViewChildren, inject } from '@angular/core';
import type { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { Title, Meta } from '@angular/platform-browser';
import type { Params } from '@angular/router';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import type { Subscription } from 'rxjs';
import { firstValueFrom, forkJoin, of, Subject } from 'rxjs';
import { debounceTime, filter, map, take } from 'rxjs/operators';

import { environment } from '../../../../enviroment/enviroment';
import { CarCardComponent } from '../../../core/car-card/car-card.component';
import { CarDetailCardComponent } from '../../../core/car-detail-card/car-detail-card.component';
import type { AlertResponse } from '../../../models/alert';
import type { ListResponse } from '../../../models/api-response';
import type { Car } from '../../../models/car';
import type { FiltersPaylod, VehicleFilters } from '../../../models/car-filters';
import type { LocationDetail } from '../../../models/location-detail';
import type { ScreenFlags } from '../../../models/screen-flags';
import { AlertService } from '../../../services/alert.service';
import { ApiService } from '../../../services/api.service';
import { FileService } from '../../../services/file-access.service';
import { LinkService } from '../../../services/link.service';
import { ResponsiveDetectionService } from '../../../services/responsive-detection.service';
import { ScrollService } from '../../../services/scroll.service';
import { SharedService } from '../../../services/shared.service';
import { SharedModule } from '../../../shared/shared.module';
import { toCamelCase } from '../../../shared/utitlities/case-conversion';
import { chunkArray } from '../../../shared/utitlities/chunk-arrays';
import { removeEmptyKeys } from '../../../shared/utitlities/validate-keys';
import { WebFacade } from '../../../store/web/web-facade.service';
import { CarFiltersComponent } from '../car-filters/car-filters';
import { WEB_CONFIG } from '../web-config';

@Component({
  selector: 'app-our-fleet',
  imports: [SharedModule, CarCardComponent, RouterModule, CarDetailCardComponent],
  templateUrl: './our-fleet.component.html',
  styleUrl: './our-fleet.component.scss',
})
export class OurFleetComponent implements OnInit, OnDestroy {
  titleService = inject(Title);
  metaService = inject(Meta);
  router = inject(Router);
  responsiveService = inject(ResponsiveDetectionService);
  sharedService = inject(SharedService);
  apiService = inject(ApiService);
  webfacadeService = inject(WebFacade);
  dialog = inject(MatDialog);
  alertService = inject(AlertService);
  _router = inject(ActivatedRoute);
  platformId = inject(PLATFORM_ID);
  linkService = inject(LinkService);
  fileAccessService = inject(FileService);
  scrollService = inject(ScrollService);
  changeDetector = inject(ChangeDetectorRef);

  cars: Car[] = [];
  allCars: Car[] = [];
  name$ = this.webfacadeService.filterNames$;
  make$ = this.webfacadeService.filterMakes$;
  transmission$ = this.webfacadeService.filterTransmission$;
  seats$ = this.webfacadeService.filterSeats$;
  sortBy = 'lowest';
  isSidebarOpen = false;
  isDesktop = false;
  screenFlagsSub: Subscription | undefined;
  sidebarSub: Subscription | undefined;
  openedFaq = 0;
  faqs: { question: string; answer: string }[] = WEB_CONFIG.faqs;
  searchTerm = '';
  private readonly searchSubject = new Subject<string>();
  filtersPayload: FiltersPaylod = {
    name: null,
    make: null,
    transmission: null,
    seats: null,
    type: null,
  };
  selectedName: string | null = null;
  selectedMake: string | null = null;
  selectedTransmission: string | null = null;
  selectedSeats: string | null = null;
  vehicleFilters: VehicleFilters = {
    name: null,
    make: null,
    transmission: null,
    seats: null,
    type: null,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    max_price: 0,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    min_price: 0,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    pickup_date: null,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    dropoff_date: null,
  };
  supportPhone = environment.ANGULAR_SUPPORT_PHONE;
  supportEmail = environment.ANGULAR_SUPPORT_EMAIL;
  carRows: Car[][] = [];
  selectedCar: Car | null = null;
  selectedRow: number | null = null;
  locationDetail!: LocationDetail;
  screenFlags: ScreenFlags = {
    isMobile: false,
    isTablet: false,
    isLaptop: false,
    isDesktop: false,
  };
  carCardFilters: Partial<LocationDetail> | null = null;
  @ViewChildren('detailSection') detailSections!: QueryList<ElementRef>;
  isBrowser = isPlatformBrowser(this.platformId);
  filterTimeoutId: ReturnType<typeof setTimeout> | null = null;
  filterDebounce: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.titleService.setTitle('Our Fleet | Qautos');
    this.metaService.updateTag({
      name: 'description',
      content: 'The premier ride experience',
    });
    this.metaService.updateTag({
      property: 'og:title',
      content: 'Our Fleet | Qautos',
    });
    this.metaService.updateTag({
      property: 'og:description',
      content: 'Explore our modern fleet available for flexible rental plans.',
    });
    this.metaService.updateTag({ property: 'og:type', content: 'website' });
    this.metaService.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });

    if (this.isBrowser) {
      this.linkService.setCanonicalUrl('/our-fleet');
    }

    this.screenFlagsSub = this.responsiveService.screenFlags$.subscribe((flags) => {
      this.isDesktop = flags.isDesktop;
      this.isSidebarOpen = this.isDesktop;
      this.screenFlags = flags;
      this.updateCarRows();
    });

    this.sidebarSub = this.sharedService.sidebarOpen$.subscribe((open) => {
      if (!this.isDesktop) {
        this.isSidebarOpen = open;
      }
    });

    this._router.queryParams.subscribe((param: Params) => {
      if (param['type']) {
        this.filtersPayload = { ...this.filtersPayload, type: param['type'] };
      }
    });

    this.webfacadeService.locationDetail$
      .pipe(filter((l: LocationDetail) => Boolean(l && l.pickupDate && l.dropoffDate)))
      .subscribe((l: LocationDetail) => {
        this.vehicleFilters.pickup_date = l.pickupDate;
        this.vehicleFilters.dropoff_date = l.dropoffDate;
        this.locationDetail = l;
      });

    this.webfacadeService.loadFilters(this.filtersPayload);
    this.getVehicles();
    this.webfacadeService.loadExtras();
    this.webfacadeService.getLocationAutoComplete('');

    this.searchSubject.pipe(debounceTime(500)).subscribe((searchTerm: string) => {
      this.applyFrontendSearch(searchTerm);
    });

    if (this.isBrowser) {
      this.handlePageRefresh(() => {
        localStorage.removeItem('web');
        this.webfacadeService.clearWebState();
      });
    }
  }

  handlePageRefresh(clearStateFn: () => void): void {
    if (!this.isBrowser) {
      return;
    }
    const HAS_VISITED = sessionStorage.getItem('hasVisited');

    if (!HAS_VISITED) {
      sessionStorage.setItem('hasVisited', 'true');
      clearStateFn();
    }

    window.addEventListener('beforeunload', () => {
      sessionStorage.removeItem('hasVisited');
    });
  }

  resetFilters(): void {
    this.selectedName = null;
    this.selectedMake = null;
    this.selectedTransmission = null;
    this.searchTerm = '';
    this.selectedSeats = null;
    this.filtersPayload = {
      name: this.selectedName,
      make: this.selectedMake,
      transmission: this.selectedTransmission,
      seats: this.selectedSeats,
      type: null,
    };
    this.webfacadeService.clearWebState();
    this.vehicleFilters.pickup_date = null;
    this.vehicleFilters.dropoff_date = null;
    this.vehicleFilters.max_price = 0;
    this.vehicleFilters.min_price = 0;
    this.triggerFilterUpdate();
  }

  onToggleFilter(): void {
    if (!this.isDesktop) {
      this.sharedService.openSidebar();
    }
  }

  triggerFilterUpdate(): void {
    if (this.filterDebounce) {
      clearTimeout(this.filterDebounce);
    }

    this.filterDebounce = setTimeout(() => {
      this.webfacadeService.loadFilters(this.filtersPayload);
      this.getVehicles();
    }, 400);
  }

  onPriceChange(): void {
    this.triggerFilterUpdate();
  }

  onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  onSearchClear(): void {
    this.searchTerm = '';
    this.searchSubject.next(this.searchTerm);
  }

  applyFrontendSearch(searchTerm: string): void {
    if (!searchTerm || searchTerm.trim() === '') {
      this.cars = [...this.allCars];
      this.updateCarRows();
      return;
    }

    const TERM = searchTerm.toLowerCase().trim();

    const SEARCH_CARS = this.allCars.filter((car: Car) => {
      return (
        (car.id && car.id.toString().toLowerCase().includes(TERM)) ||
        (car.model && car.model.toLowerCase().includes(TERM)) ||
        (car.make && car.make.toLowerCase().includes(TERM)) ||
        (car.name && car.name.toLowerCase().includes(TERM)) ||
        (car.chasisNo && car.chasisNo.toLowerCase().includes(TERM))
      );
    });

    this.cars = SEARCH_CARS;
    this.updateCarRows();
  }

  async getVehicles(): Promise<void> {
    Object.assign(this.vehicleFilters, this.filtersPayload);
    const FILTERS = removeEmptyKeys(this.vehicleFilters);
    const SORT_ORDER = this.sortBy === 'lowest' ? 'ASC' : 'DESC';
    const URL = `${WEB_CONFIG.getVehicles}?sort_by=daily_rate&sort_order=${SORT_ORDER}`;
    this.selectedCar = null;
    this.selectedRow = null;

    // if (this.searchTerm) {
    //   url = `${url}&search_key=${this.searchTerm}`;
    // }

    this.apiService
      .post<ListResponse<Car>>(URL, { ...FILTERS }, true)
      .subscribe(async (response) => {
        if (response?.statusCode !== 200) {
          return;
        }

        const LIST = (toCamelCase(response.data.list) || []) as Car[];
        // sorting cars as 1st featured, 2nd hot deals and then others
        const GET_PRIORITY = (car: Car): number => {
          if (car.featured && !car.hotDeal) {
            return 1;
          }
          if (car.featured && car.hotDeal) {
            return 2;
          }
          if (!car.featured && car.hotDeal) {
            return 3;
          }
          return 4;
        };

        const SORTED_CARS = [...LIST].sort((a, b) => GET_PRIORITY(a) - GET_PRIORITY(b));

        // Keep original alert for UAE
        if (
          response.data.message ===
          "No cars available. We’re sorry, but the companies we work with in UAE don't have any cars available."
        ) {
          this.alertService.error({
            heading: 'No cars available',
            message:
              'We’re sorry, but the companies we work with in the Qatar currently don’t have any cars available.',
          });
          this.allCars = [];
          this.cars = [];
          this.updateCarRows();
          return;
        }

        // Map cars to signed URLs
        this.cars = await firstValueFrom(
          forkJoin(
            SORTED_CARS.map((car) =>
              forkJoin(
                (car.images?.length ? car.images : [null]).map((img) => {
                  if (!img?.documentPath) {
                    return of(environment.ANGULAR_DEFAULT_PLACEHOLDER);
                  }

                  return this.fileAccessService.getSignedUrl$(img.documentPath);
                })
              ).pipe(map((urls) => ({ ...car, carImages: urls })))
            )
          )
        );

        this.allCars = [...this.cars];

        // Apply frontend search if needed
        if (this.searchTerm && this.searchTerm.trim() !== '') {
          this.applyFrontendSearch(this.searchTerm);
        } else {
          this.updateCarRows();
        }
      });
  }

  trackByCar(index: number, car: Car): number {
    return car.id;
  }

  getfilters(): void {
    this.webfacadeService.loadFilters(this.filtersPayload);
  }

  onSeatSelectionChange(seat: number, event: MatCheckboxChange): void {
    if (!Array.isArray(this.filtersPayload.seats)) {
      this.filtersPayload.seats = [];
    }

    if (event.checked) {
      if (!this.filtersPayload.seats.includes(seat)) {
        this.filtersPayload.seats.push(seat);
      }
    } else {
      this.filtersPayload.seats = this.filtersPayload.seats.filter((s: number) => s !== seat);
    }

    if (Array.isArray(this.filtersPayload.seats) && this.filtersPayload.seats.length === 0) {
      this.filtersPayload.seats = null;
    }

    if (this.filterTimeoutId) {
      clearTimeout(this.filterTimeoutId);
    }

    this.filterTimeoutId = setTimeout(() => {
      this.triggerFilterUpdate();
    }, 500);
  }

  onNameChange(value: string | null): void {
    this.selectedName = value;
    this.filtersPayload = { ...this.filtersPayload, name: value };
    this.triggerFilterUpdate();
  }

  onMakeChange(value: string | null): void {
    this.selectedMake = value;
    this.filtersPayload = { ...this.filtersPayload, make: value };
    this.triggerFilterUpdate();
  }

  onTransmissionChange(value: string | null): void {
    this.selectedTransmission = value;
    const LOWER = value ? value.toLowerCase() : null;
    this.filtersPayload = { ...this.filtersPayload, transmission: LOWER };
    this.triggerFilterUpdate();
  }

  onCarSelect(car: Car, rowIndex: number): void {
    if (this.selectedCar?.id === car.id) {
      this.selectedCar = null;
      this.selectedRow = null;
    } else {
      this.onCarsFilters(car, rowIndex);
    }
  }

  onCarsFilters(carDetail: Car, rowIndex: number): void {
    this.webfacadeService.locationDetail$
      .pipe(take(1))
      .subscribe((locationDetail: LocationDetail) => {
        const HAS_GLOBAL_FILTERS = !!locationDetail?.pickupDate && !!locationDetail?.dropoffDate;

        if (HAS_GLOBAL_FILTERS) {
          this.selectedCar = carDetail;
          this.selectedRow = rowIndex;
          this.changeDetector.detectChanges();

          if (this.isBrowser) {
            setTimeout(() => {
              this.scrollService.scrollToElementByIndex(this.detailSections, rowIndex, 80);
            }, 150);
          }
          return;
        } else {
          this.hasNotGlobalFilters(carDetail, rowIndex);
        }
      });
  }

  hasNotGlobalFilters(carDetail: Car, rowIndex: number): void {
    const DIALOG_REF = this.dialog.open(CarFiltersComponent, {
      width: '500px',
      height: 'auto',
      autoFocus: false,
      hasBackdrop: true,
      data: { id: carDetail.id },
    });

    DIALOG_REF.afterClosed()
      .pipe(take(1))
      .subscribe((result: AlertResponse) => {
        if (result.key) {
          const UPDATED_FILTERS = result.data as LocationDetail;
          if (UPDATED_FILTERS) {
            this.carCardFilters = removeEmptyKeys(UPDATED_FILTERS);
          }
          this.selectedCar = carDetail;
          this.selectedRow = rowIndex;
          this.changeDetector.detectChanges();
          if (this.isBrowser) {
            setTimeout(() => {
              this.scrollService.scrollToElementByIndex(this.detailSections, rowIndex, 80);
            }, 150);
          }
        }
      });
  }

  updateCarRows(): void {
    if (!this.cars?.length) {
      return;
    }

    const SIZE = this.screenFlags.isMobile ? 1 : this.screenFlags.isTablet ? 2 : 3;
    this.carRows = chunkArray(this.cars, SIZE);
  }

  onCloseCard(): void {
    this.selectedCar = null;
  }

  trackByValue(index: number, item: string): string {
    return item;
  }

  trackByValueTransmission(index: number, item: string): string {
    return item;
  }

  trackByValueSeats(index: number, item: number): number {
    return item;
  }

  trackByCarRows(index: number): number {
    return index;
  }

  trackByCarFaqs(index: number): number {
    return index;
  }

  ngOnDestroy(): void {
    this.screenFlagsSub?.unsubscribe();
    this.sidebarSub?.unsubscribe();
  }
}
